import { Action, Selector, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { CloseDialog, GetRating, OpenDialog, PostComment, PostRating, SetRating } from './rating.actions';
import { patch } from '@ngxs/store/operators';
import { Record } from '../../../../models/record';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { ToastService } from 'src/app/_core/services/toast.service';
import { Status } from 'src/app/models/comment';
import { tap } from 'rxjs';
import { UserVote } from 'src/app/models/rating';

export type RatingStateModel = {
  record: Record | null;
  userVote: number;
  hasUserVoted: boolean;
  open: boolean;
};

export const initialState: RatingStateModel = {
  record: null,
  userVote: 0,
  hasUserVoted: false,
  open: false,
};

@State<RatingStateModel>({
  name: 'rating',
  defaults: initialState,
})
@Injectable()
export class RatingState {

  private readonly apiService = inject(ApiService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);

  @Selector()
  static open(state: RatingStateModel): boolean {
    return state.open;
  }

  @Selector()
  static userVote(state: RatingStateModel): number {
    return state.userVote;
  }

  @Selector()
  static hasUserVoted(state: RatingStateModel): boolean {
    return state.hasUserVoted;
  }

  @Action(OpenDialog)
  openDialog(context: StateContext<RatingStateModel>, { record }: OpenDialog) {
    context.dispatch(new GetRating(record));
  }

  @Action(CloseDialog)
  closeDialog(context: StateContext<RatingStateModel>) {
    context.setState(patch(initialState));
  }

  @Action(PostComment)
  postComment(context: StateContext<RatingStateModel>, { comment }: PostComment) {
    const state = context.getState();

    return this.apiService.commentResource(state.record.id, this.authService.getLoggedUser().id, comment)
      .pipe(
        tap((_comment) => {
          if (_comment.status === Status.PENDING) {
            this.toastService.showToast('messages.comments.approval', 'info');
            return;
          }

          this.toastService.showToast('messages.comments.submitted');
        }));
  }

  @Action(PostRating)
  postRating(context: StateContext<RatingStateModel>) {
    const state = context.getState();
    const userId = this.authService.getLoggedUser().id;

    return this.apiService.rateRecord(state.record.id, userId, state.userVote, state.record.isbn)
      .pipe(tap(() => this.toastService.showToast('messages.rating.rate')));
  }

  @Action(SetRating)
  setRating(context: StateContext<RatingStateModel>, { rate }: SetRating) {
    const state = context.getState();

    context.setState(
      patch({
        ...state,
        userVote: rate,
        hasUserVoted: true,
      }),
    );
  }

  @Action(GetRating)
  getRating(context: StateContext<RatingStateModel>, { record }: GetRating) {
    const userId = this.authService.getLoggedUser().id;

    return this.apiService.getRecordRating(record.id, record.isbn, userId)
      .pipe(
        tap((rating) => {
          let userVote: UserVote = null;

          if (rating) {
            userVote = rating.userIdAndVoteList.find((v) => v.userId === userId);
          }

          if (userVote) {
            context.setState(
              patch({
                open: true,
                record,
                userVote: userVote.vote,
                hasUserVoted: true,
              }),
            );
            return;
          }

          context.setState(
            patch({
              open: true,
              record,
              userVote: rating ? rating.mean : 0,
              hasUserVoted: false,
            }),
          );
        })
      );
  }
}
